import { useContext, useMemo } from "react";
import { trpc } from "../../../trpc";
import MerchantContext from "../../../contexts/MerchantContext";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

type StepWhatsappAndAgentProps = {
  enableAiAgent: boolean;
  onEnableAiAgentChange: (enabled: boolean) => void;
  selectedWhatsappConnectionId: string;
  onSelect: (whatsappConnectionId: string) => void;
};

const StepWhatsappAndAgent = ({
  enableAiAgent,
  onEnableAiAgentChange,
  selectedWhatsappConnectionId,
  onSelect,
}: StepWhatsappAndAgentProps) => {
  const { merchantId } = useContext(MerchantContext);

  const { data: connections, isLoading } = trpc.whatsappConnections.list.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  const { data: agents } = trpc.agents.list.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  const enabledConnections = useMemo(
    () => (connections ?? []).filter((c) => !c.disabled),
    [connections]
  );

  const connectionIdsWithAgent = useMemo(
    () => new Set((agents ?? []).map((a) => a.whatsappConnectionId)),
    [agents]
  );

  const selectedHasAgent = connectionIdsWithAgent.has(selectedWhatsappConnectionId);

  const handleSelectConnection = (connectionId: string) => {
    onSelect(connectionId);
    onEnableAiAgentChange(connectionIdsWithAgent.has(connectionId));
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="mb-2 text-lg font-bold">WhatsApp y agente</p>
        <p className="text-sm text-gray-600">
          Elige desde qué línea se enviará y si el agente AI responderá automáticamente.
        </p>
      </div>

      <div>
        <p className="mb-2 text-sm font-medium text-gray-700">Línea de WhatsApp</p>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size="lg" />
          </div>
        ) : enabledConnections.length === 0 ? (
          <Card>
            <CardContent className="p-6">
              <p className="py-4 text-center text-sm text-gray-500">
                No hay líneas de WhatsApp disponibles.
              </p>
            </CardContent>
          </Card>
        ) : (
          <RadioGroup value={selectedWhatsappConnectionId} onValueChange={handleSelectConnection}>
            <div className="flex flex-col gap-3">
              {enabledConnections.map((connection) => (
                <Card
                  key={connection.id}
                  className={cn(
                    "cursor-pointer border-2 transition-all hover:border-purple-300 hover:bg-purple-50/50",
                    selectedWhatsappConnectionId === connection.id
                      ? "border-purple-500 bg-purple-50"
                      : "border-gray-200"
                  )}
                  onClick={() => handleSelectConnection(connection.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value={connection.id} />
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-medium">
                            {connection.phoneNumber || connection.phoneNumberId}
                          </p>
                          {connection.isPrimary && (
                            <Badge
                              className="border-green-200 bg-green-100 text-xs text-green-800"
                              variant="outline"
                            >
                              Principal
                            </Badge>
                          )}
                          {connectionIdsWithAgent.has(connection.id) ? (
                            <Badge
                              className="border-purple-200 bg-purple-100 text-xs text-purple-800"
                              variant="outline"
                            >
                              Con agente IA
                            </Badge>
                          ) : (
                            <Badge
                              className="border-gray-200 bg-gray-100 text-xs text-gray-800"
                              variant="outline"
                            >
                              Sin agente
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </RadioGroup>
        )}
      </div>

      {selectedWhatsappConnectionId && selectedHasAgent && (
        <div>
          <p className="mb-2 text-sm font-medium text-gray-700">Agente IA</p>
          <div className="grid gap-3 md:grid-cols-2">
            <Card
              className={cn(
                "min-h-11 cursor-pointer border-2 transition-all hover:border-purple-300",
                enableAiAgent ? "border-purple-500 bg-purple-50" : "border-gray-200"
              )}
              onClick={() => onEnableAiAgentChange(true)}
            >
              <CardContent className="p-4">
                <p className="font-medium">Agente IA activo</p>
                <p className="mt-1 text-sm text-gray-600">
                  El agente AI responderá automáticamente cuando el cliente conteste.
                </p>
              </CardContent>
            </Card>
            <Card
              className={cn(
                "min-h-11 cursor-pointer border-2 transition-all hover:border-purple-300",
                !enableAiAgent ? "border-purple-500 bg-purple-50" : "border-gray-200"
              )}
              onClick={() => onEnableAiAgentChange(false)}
            >
              <CardContent className="p-4">
                <p className="font-medium">Agente IA inactivo</p>
                <p className="mt-1 text-sm text-gray-600">
                  Las respuestas irán al buzón humano. El envío se asignará a ti.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default StepWhatsappAndAgent;
